import {
  NOT
} from '../../Boolean.lib';
import Core from '../../Core';
import globalErrorHandler from '../../Error/globalErrorHandler.fun';
import Http from '../../Http';
import {
  getStateModel
} from '../../models/Models.lib';
import {
  isObject,
  Obj
} from '../../Object.lib';
import {
  compileText,
  isNonEmptyString,
  trim
} from '../../String.lib';

export const COLLECTION__ACCOUNT_EMPLOYERS = 'AccountEmployers';
export const COLLECTION__ACCOUNT_SUBROLES = 'AccountSubroles';
export const COLLECTION__ACCOUNTS = 'Accounts';
export const COLLECTION__ACCOUNTS_STARRED = 'AccountsStarred';
export const COLLECTION__CANDIDATES = 'Candidates';
export const COLLECTION__CANDIDATES_STARRED = 'CandidatesStarred';
export const COLLECTION__COMPANY = 'Company';
export const COLLECTION__CREDENTIALS = 'Credentials';
export const COLLECTION__EMPLOYER_ATS_MAPPING_RULES = 'EmployerAtsMappingRules';
export const COLLECTION__EMPLOYER_BLACK_LISTS = 'EmployerBlackLists';
export const COLLECTION__EMPLOYER_EMAIL_TEMPLATES = 'EmployerEmailTemplates';
export const COLLECTION__EMPLOYER_SOURCE_LISTS = 'EmployerSourceLists';
export const COLLECTION__EMPLOYERS = 'Employers';
export const COLLECTION__EMPLOYERS_STARRED = 'EmployersStarred';
export const COLLECTION__ENGAGEMENTS = 'Engagements';
export const COLLECTION__ENGAGEMENTS_STARRED = 'EngagementsStarred';
export const COLLECTION__JOB_SOURCE_LISTS = 'JobSourceLists';
export const COLLECTION__JOBS = 'Jobs';
export const COLLECTION__JOBS_STARRED = 'JobsStarred';
export const COLLECTION__MATCH_DECISION_TABLES = 'MatchDecisionTables';
export const COLLECTION__PRESCREEN_QUESTION_TEMPLATES = 'PrescreenQuestionTemplates';
export const COLLECTION__RECRUITER_WHITE_LISTS = 'RecruiterWhiteLists';
export const COLLECTION__SCHOOL = 'School';
export const COLLECTION__SOVREN_DATA = 'SovrenData';
export const COLLECTION__TEMPLATES = 'Templates';

/**
 * CREATE
 * @param {object} options
 * @param {string} options.collection -table name
 * @param {string} options.model      -model base, if set then it will map the record using model fields
 * @param {string} options.record     -record to be post in the DB
 * @param {function} options.mapper   -routine to do some mapping operation to the results
 */
export async function createLoopbackRecord(options) {
  let { collection, model, record, mapper } = options;
  if (
    !isNonEmptyString(collection) ||
    !isObject(record)
  ) { return null; }
  if (isObject(model)) {
    record = getStateModel(record, model)
  }
  return Http.post(Core.getApi(collection), record).then((response) => {
    if (mapper instanceof Function) {
      response = mapper(response);
    }
    return response;
  });
}

/**
 * Reads records from a LoopBack collection using dynamic query parameters.
 *
 * This function handles both `GET` and other HTTP methods to retrieve records
 * from LoopBack-based APIs. It validates required parameters, constructs a
 * filter query, and optionally maps or filters the response.
 *
 * @async
 * @function readLoopbackRecord
 * @param {Object} params - The function parameters.
 * @param {string} params.collection - The LoopBack collection or endpoint name.
 * @param {string} [params.method='get'] - The HTTP method to use (`get`, `post`, etc.).
 * @param {Object} [params.where] - The query `where` clause used for filtering results.
 * @param {Object|false} [params.propsWhere] - Overrides `where`. If explicitly set
 * to `false`, the query skips `where` validation.
 * @param {string[]|Object} [params.fields] - The specific fields to return in results.
 * @param {number} [params.limit] - The maximum number of records to retrieve.
 * @param {number} [params.offset] - The number of records to skip.
 * @param {string|string[]} [params.order] - Sorting order (e.g. `"createdAt DESC"`).
 * @param {string[]|Object[]} [params.include] - Related models or relations to include.
 * @param {Function} [params.filter] - Optional function applied to the raw response.
 * @param {Function} [params.mapper] - Optional function to transform the final result.
 * @returns {Promise<Object|Object[]|null>} The retrieved record(s), or `null` if validation fails.
 * @param {...Object} [params.rest] - Additional request options passed to `Http[method]`.
 *
 * @example
 * // Fetch candidate by ID
 * const candidate = await readLoopbackRecord({
 *   collection: 'candidates',
 *   where: { id: 42 },
 *   limit: 1
 * });
 *
 * @example
 * // Fetch with custom mapper
 * const jobs = await readLoopbackRecord({
 *   collection: 'jobs',
 *   where: { active: true },
 *   mapper: (res) => res.map(job => job.title)
 * });
 */
export async function readLoopbackRecord({
  collection = '',
  method = 'get',
  where = undefined,
  where: propsWhere = undefined,
  fields = undefined,
  limit = undefined,
  offset = undefined,
  order = undefined,
  include = undefined,
  filter = undefined,
  mapper = undefined,
  ...rest
}) {
  where = Obj(where);
  if (
    // NOT collection set
    NOT(trim(collection)) ||
    // OR
    (
      // where is NOT false
      (propsWhere !== false) &&
      // AND collection is NOT a path
      NOT(trim(collection).includes('/')) &&
      // AND where is NOT empty
      NOT(Object.keys(where).length) &&
      // AND collection is NOT in the list of main entities
      [
        COLLECTION__ACCOUNTS,
        COLLECTION__EMPLOYERS,
        COLLECTION__JOBS,
        COLLECTION__CANDIDATES,
        COLLECTION__ENGAGEMENTS
      ].includes(collection)
      && NOT(limit)
    )
  ) {
    globalErrorHandler(compileText(
      'Query{{KEY}} - Collection and where parameters are required',
      { KEY: rest.key ? `(${rest.key})` : '' }
    ));
    return null;
  }
  const query = {
    ...rest,
    where,
    fields,
    limit,
    offset,
    order,
    include
  };
  const params = (
    method === 'get'
      ? { filter: JSON.stringify(query) }
      : query
  );
  return Http[method](Core.getApi(collection), params).then((response) => {
    if (filter instanceof Function) {
      response = filter(response);
    }
    if (limit === 1) {
      if (Array.isArray(response)) { response = response[0]; }
    }
    if (mapper instanceof Function) {
      response = mapper(response);
    }
    return response;
  });
}
export const getLoopbackRecord = readLoopbackRecord;

/**
 * Updates a single LoopBack record via PATCH request.
 *
 * @async
 * @function updateLoopbackRecord
 * @param {Object} options - Configuration object.
 * @param {string} options.collection - Name of the LoopBack collection or model endpoint.
 * @param {string|number} options.id - Unique record identifier to update.
 * @param {Object} [options.model] - Optional model definition used to map or validate record data.
 * @param {Object} options.record - Data object containing fields to update.
 * @param {Function} [options.mapper] - Optional function to transform the response before returning.
 * @param {string} [options.key='updateLoopbackRecord'] - Identifier key for request tracking or logging.
 * @returns {Promise<Object|null>} Resolves with the updated record response or null if invalid input.
 * @param {...Object} [options.more] - Additional fetch parameters (headers, credentials, etc.).
 * 
 * @description
 * - Sends a PATCH request to update a specific record in a LoopBack collection.
 * - Optionally maps record data using a model before submission.
 * - Allows response transformation through a mapper function.
 * - Automatically attaches authorization and tracking headers.
 * - Returns `null` if required parameters are invalid.
 */
export async function updateLoopbackRecord(options) {
  let {
    collection,
    id,
    model,
    record,
    mapper,
    key = 'updateLoopbackRecord',
    ...more
  } = options;

  if (
    !isNonEmptyString(collection) ||
    !isObject(record)
  ) { return null; }
  if (isObject(model)) {
    record = getStateModel(record, model);
  }
  try {
    const response = await fetch(Core.getApi(`${collection}/${id}`), {
      ...more,
      method: 'PATCH',
      headers: {
        Authorization: Core.getAccessToken(),
        'Content-type': 'application/json; charset=UTF-8',
        key,
        ...more.headers,
      },
      body: JSON.stringify(record)
    });

    // Optionally handle non-200 responses
    if (!response.ok) {
      console.error(`${key}: HTTP ${response.status}`, response.statusText);
      throw new Error(`Failed to update record: ${response.status}`);
    }

    // Safely parse the response body
    let data;
    try {
      data = await response.json();
    } catch {
      data = null;
    }

    // Optionally map result
    if (typeof mapper === 'function') {
      data = mapper(data);
    }
    return data;
  }
  catch (error) {
    console.error(`${key}:`, error);
    throw error;
  }
}

/**
 * DELETE
 * @param {object} options
 * @param {string} options.collection -table name
 * @param {string} options.id         -record id
 * @param {function} options.mapper   -routine to do some mapping operation to the results
 */
export async function deleteLoopbackRecord(options) {
  let { collection, id, mapper } = options;
  if (
    !isNonEmptyString(id)
  ) { return null; }
  return Http.delete(Core.getApi(`${collection}/${id}`)).then((response) => {
    if (mapper instanceof Function) {
      response = mapper(response);
    }
    return response;
  });
}
